import { Box, Button, Alert } from "@mui/material";
import { JSONContent } from "@tiptap/react";
import SimpleRichEditor, { Handle } from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import useCached from "components/hooks/useCached";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useCreateFamilyAnnotation } from "services/engagement";
import FlagsSelect from "./FlagsSelect";
import TypeSelect from "./TypeSelect";
import { Flag } from "../flags";
import { DatePicker } from "@mui/x-date-pickers";

export interface AddAnnotationRef {
  changeNoteType: (type: string) => void;
}

const AddAnnotation = forwardRef<
  AddAnnotationRef,
  {
    familyRef: string;
    signupSessionRef: string;
    noteTypes: string[];
    includeFlags?: boolean;
    placeholder?: string;
    onUpdate: () => Promise<void>;
    forceOpen?: boolean;
    flagOptions?: Flag[];
  }
>(
  (
    {
      familyRef,
      signupSessionRef,
      onUpdate,
      noteTypes,
      includeFlags = true,
      placeholder = "Add family note",
      forceOpen = true,
      flagOptions,
    },
    ref,
  ) => {
    const editorRef = useRef<Handle>(null);
    const [content, setContent] = useCached<RichContent | null>(
      `admin-annotation-l${signupSessionRef}-f${familyRef}`,
      null,
    );
    const [_initialContent] = useState<string | JSONContent | undefined>(
      (content?.text?.length && content?.json && JSON.parse(content?.json)) ||
        undefined,
    );
    const [noteType, setNoteType] = useState(noteTypes[0]);
    const [flags, setFlags] = useState<string[]>([]);
    const [flagUntilDate, setFlagUntilDate] = useState<Date | null>(null);
    const [dateOpen, setDateOpen] = useState(false);
    const [annotationKey, setAnnotationKey] = useState("");
    const [uploadsInProgress, setUploadsInProgress] = useState(false);
    const { request, loading } = useCreateFamilyAnnotation();

    useImperativeHandle(ref, () => ({
      changeNoteType: (type: string) => {
        setNoteType(type);
        if (type === "billing") {
          const oneMonthFromNow = new Date();
          oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
          setFlagUntilDate(oneMonthFromNow);
        }
        editorRef.current?.focus();
      },
    }));

    const addAnnotation = async () => {
      if (!content) return;
      const resp = await request({
        familyRef: familyRef,
        signupSessionRef: signupSessionRef,
        contentType: "json",
        content: content.json,
        textContent: content.text,
        attachments: content.attachments,
        noteType: noteType,
        flags: flags,
        flagUntilDate: flagUntilDate
          ? flagUntilDate.toLocaleDateString("en-US")
          : "",
      });
      if (!resp?.annotation) return;
      setContent(null);
      setAnnotationKey(resp.annotation.ref);
      setFlags([]);
      setFlagUntilDate(null);
      onUpdate();
    };

    useEffect(() => {
      setContent(null);
      setAnnotationKey(`admin-annotation-l${signupSessionRef}-f${familyRef}`);
      setFlags([]);
      setFlagUntilDate(null);
    }, [signupSessionRef, familyRef]);

    useEffect(() => {
      if (noteType === "billing" && flags.length > 0) {
        const oneMonthFromNow = new Date();
        oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
        setFlagUntilDate(oneMonthFromNow);
      }
    }, [noteType]);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <SimpleRichEditor
          forceOpen={forceOpen}
          ref={editorRef}
          key={annotationKey}
          disabled={loading}
          setContent={setContent}
          placeholder={placeholder}
          setUploadsInProgress={setUploadsInProgress}
          initialContent={_initialContent}
          primaryAction={
            <Button
              size="small"
              sx={{
                height: "unset",
                padding: "8px 18px",
              }}
              onClick={addAnnotation}
              disabled={loading || !content || uploadsInProgress}
            >
              Add
            </Button>
          }
          leftActionsPrefix={
            <>
              <TypeSelect
                defaultType={noteType}
                setType={setNoteType}
                typeOptions={noteTypes}
              />
              {includeFlags && (
                <FlagsSelect
                  flagOptions={flagOptions}
                  flags={flags}
                  setFlags={setFlags}
                />
              )}
            </>
          }
        />
        {includeFlags && flags.length > 0 && (
          <DatePicker
            sx={{
              width: "100%",
              ".MuiButtonBase-root": { marginRight: "unset" },
            }}
            label="Flag until date"
            value={flagUntilDate}
            onChange={(date) => {
              setFlagUntilDate(date);
            }}
            format="MM/dd/yyyy"
            open={dateOpen}
            onClose={() => setDateOpen(false)}
            slotProps={{
              textField: {
                size: "small",
                helperText:
                  "Optionally highlight this note until a specific date",
                onClick: () => setDateOpen(true),
              },
            }}
          />
        )}
        {noteType === "consultation" && (
          <Alert severity="warning">
            Consultation notes are included in the lead assignment description.
          </Alert>
        )}
      </Box>
    );
  },
);

export default AddAnnotation;
